// components/pages/CollectionPage.js
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CollectionModal } from '../../hooks/useCollections';
import { useCollectionPage } from '../../hooks/useCollectionPage';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import { usePageSearch } from '../navigation/SearchContext';
import { 
  TagFilter, 
  CollectionContent, 
  EditNoteModal,
  LoadingSpinner 
} from '../collection/CollectionComponents';

// Inline useNoteEditor hook
const useNoteEditor = (loadCollection) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editNote, setEditNote] = useState('');

  const openEditModal = useCallback((item) => {
    setEditingItem(item);
    setEditNote(item.note || '');
    setEditModalOpen(true);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingItem(null);
    setEditNote('');
  }, []);

  const saveEditedNote = useCallback(() => {
    if (!editingItem) return false;
    
    try {
      // Handle both old and new format updates
      if (typeof editingItem.id === 'string' && editingItem.id.startsWith('migrated_')) {
        // For migrated items, update the old localStorage format
        const parts = editingItem.id.split('_');
        const originalIndex = parseInt(parts[1]);
        const collectionCounter = localStorage.getItem('collectionNumber');
        const indexToUpdate = collectionCounter - originalIndex;
        
        localStorage.setItem('itemNote'.concat(indexToUpdate), editNote);
      }
      
      // Also update new format if it exists
      const collectionData = JSON.parse(localStorage.getItem('userCollection') || '[]');
      const updatedCollection = collectionData.map(item => 
        item.id === editingItem.id 
          ? { ...item, note: editNote }
          : item
      );
      
      localStorage.setItem('userCollection', JSON.stringify(updatedCollection));
      
      // Close modal and refresh collection data without page reload
      closeEditModal();
      
      // Trigger collection refresh if provided
      if (loadCollection) {
        loadCollection();
      }
      
      return true;
    } catch (error) {
      console.error('Error updating note:', error);
      return false;
    }
  }, [editingItem, editNote, closeEditModal, loadCollection]);

  return {
    editModalOpen,
    editingItem,
    editNote,
    setEditNote,
    openEditModal,
    closeEditModal,
    saveEditedNote
  };
};

/**
 * Modern Collection Page Component with Integrated Search
 * Shows ALL collection items with migration support for old format
 */
const CollectionPage = () => {
  const { t } = useTranslation('common');

  // Custom hooks for functionality
  const {
    collection,
    filteredCollection,
    availableTags,
    loading,
    searchTerm,
    setSearchTerm,
    selectedTags,
    toggleTag,
    clearFilters,
    handleRemoveItem,
    loadCollection,
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    closeModal,
    handleSave,
    isStorageAvailable
  } = useCollectionPage();
  
  // Pass loadCollection to useNoteEditor so it can refresh after save
  const {
    editModalOpen,
    editingItem,
    editNote,
    setEditNote,
    openEditModal,
    closeEditModal,
    saveEditedNote
  } = useNoteEditor(loadCollection);

  // Handle search changes from the nav bar
  const handleSearchChange = useCallback((query) => {
    setSearchTerm(query);
  }, [setSearchTerm]);

  // Take over the global integrated (bottom) search bar while this page is
  // mounted, so it filters the collection in-page (setSearchTerm) instead of
  // navigating to /search. Clearing the bar (× or empty) calls onSearch(''),
  // which resets the filter and shows the full collection again.
  usePageSearch({
    placeholder: t('common:searchYourCollection', 'Search your collection...'),
    onSearch: handleSearchChange,
    value: searchTerm,
  });

  // Handle saving edited note - no reload needed
  const handleSaveEditedNote = useCallback(() => {
    saveEditedNote();
  }, [saveEditedNote]);

  if (loading) {
    return (
      <>
        <style>{collectionPageStyles}</style>
        <div id="homebg">
          <IntegratedSearchNavBar />
          <LoadingSpinner />
        </div>
      </>
    );
  }

  return (
    <>
      <style>{collectionPageStyles}</style>

      <div id="homebg">
        {/* Integrated Search Navigation */}
        <IntegratedSearchNavBar />

        {/* Navbar Spacer for fixed navbar */}
        <div id="mobileNavbarSpacer"></div>

        {/* Collection Stats */}
        {collection.length > 0 && (
          <div className="collection-stats">
            {filteredCollection.length === collection.length 
              ? t('common:itemsInCollection', '{{count}} item in your collection', { count: collection.length })
              : t('common:showingOfItems', 'Showing {{shown}} of {{total}} items', { shown: filteredCollection.length, total: collection.length })
            }
          </div>
        )}

        {/* Search Results Info */}
        {searchTerm && (
          <div className="search-results-info">
            {filteredCollection.length === 0 
              ? t('common:noItemsFound', 'No items found for "{{searchTerm}}"', { searchTerm })
              : t('common:foundItemsMatching', 'Found {{count}} item matching "{{searchTerm}}"', { count: filteredCollection.length, searchTerm })
            }
          </div>
        )}
        
        {/* Tag Filters */}
        <TagFilter
          availableTags={availableTags}
          selectedTags={selectedTags}
          onToggleTag={toggleTag}
          onClearFilters={clearFilters}
        />

        {/* Collection Content - Shows ALL items or filtered results */}
        <CollectionContent
          collection={collection}
          filteredCollection={filteredCollection}
          onEditNote={openEditModal}
          onRemove={handleRemoveItem}
          onClearFilters={clearFilters}
        />

        {/* Bottom spacing for mobile navbar */}
        <div className="bottom-spacing"></div>

        {/* Edit Note Modal */}
        <EditNoteModal
          isOpen={editModalOpen}
          item={editingItem}
          note={editNote}
          onNoteChange={setEditNote}
          onSave={handleSaveEditedNote}
          onClose={closeEditModal}
        />

        {/* Collection Modal (from useCollections hook) */}
        <CollectionModal
          isModalOpen={isModalOpen}
          currentItem={currentItem}
          saveSuccess={saveSuccess}
          note={note}
          setNote={setNote}
          handleSave={handleSave}
          closeModal={closeModal}
          isStorageAvailable={isStorageAvailable}
        />
      </div>
    </>
  );
};

// Extracted styles for the collection page - Now theme-aware
const collectionPageStyles = `
  /* ============================================
     Base Page Styles
     ============================================ */
  #homebg {
    background-color: var(--bg-primary) !important;
    padding: 0 !important;
    min-height: 100vh;
    color: var(--text-primary);
  }

  /* ============================================
     Collection Item Container
     ============================================ */
  #feedContainerFeedPage {
    padding: 20px;
    border-radius: 18px;
    margin-top: 0em;
    margin-bottom: 0.5rem;
    background-color: var(--bg-card);
    box-shadow: 10px 10px 10px var(--shadow-color);
  }

  #feedContainerFeedPage:hover {
    background-color: var(--bg-card-hover);
  }

  /* ============================================
     Text Styles
     ============================================ */
  #billText {
    font-size: 16px;
    font-family: var(--site-font);
    color: var(--text-primary);
  }

  /* ============================================
     Button Styles
     ============================================ */
  .button-container {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
    flex-wrap: wrap;
    clear: both;
  }
  
  .button-container Button,
  #billLink,
  #viewBillButton,
  #addToCollectionButton,
  #shareButton {
    text-align: center;
    padding: 8px 14px;
    color: var(--text-primary);
    border: 0px;
    box-shadow: 2px 2px 12px var(--shadow-color);
    background-color: var(--bg-button);
    border-radius: 20px;
    font-family: var(--site-font);
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
  }

  .button-container Button:hover,
  #billLink:hover {
    background-color: var(--bg-button-hover);
    transform: translateY(-1px);
  }

  .button-container Button:active,
  #billLink:active {
    transform: translateY(0);
  }

  /* ============================================
     Collection Item Wrapper
     ============================================ */
  .collection-item-wrapper {
    margin-bottom: 0.5rem;
    padding: 0 1rem;
  }

  /* ============================================
     Stats and Info Text
     ============================================ */
  .collection-stats {
    color: var(--text-tertiary);
    text-align: center;
    padding: 0.75rem 1rem 0.25rem;
    font-size: 14px;
    font-family: var(--site-font);
  }

  .search-results-info {
    color: var(--text-secondary);
    text-align: center;
    padding: 0.25rem 1rem 0.5rem;
    font-size: 14px;
    font-family: var(--site-font);
  }

  /* ============================================
     Note Display Styles (Clickable)
     ============================================ */
  .note-display {
    margin-top: 0.75rem;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .note-display:hover {
    background-color: var(--bg-card-hover);
  }

  .note-display strong {
    color: var(--text-secondary);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .note-display p {
    margin: 4px 0 0 0;
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.4;
  }

  /* ============================================
     Tag Badge Styles
     ============================================ */
  .tag-badge {
    background-color: var(--bg-button-secondary);
    color: var(--text-primary);
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    margin-right: 6px;
    margin-top: 8px;
    display: inline-block;
    border: 1px solid var(--border-secondary);
  }

  /* ============================================
     Mobile Spacing
     ============================================ */
  #mobileNavbarSpacer {
    height: 70px;
  }

  .bottom-spacing {
    height: 120px;
  }

  /* ============================================
     Empty State Styles
     ============================================ */
  .empty-collection-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    padding: 2rem;
    text-align: center;
  }

  .empty-collection-container h4 {
    color: var(--text-primary);
    font-family: var(--site-font);
    margin-bottom: 0.5rem;
  }

  .empty-collection-container p {
    color: var(--text-tertiary);
    font-family: var(--site-font);
  }

  /* ============================================
     Loading Spinner
     ============================================ */
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 50vh;
  }

  /* ============================================
     Responsive Adjustments
     ============================================ */
  @media (max-width: 576px) {
    #feedContainerFeedPage {
      padding: 15px;
      margin-left: 0.5rem;
      margin-right: 0.5rem;
    }

    .collection-item-wrapper {
      padding: 0 0.5rem;
    }

    .button-container {
      gap: 8px;
    }

    .button-container Button,
    #billLink {
      padding: 6px 12px;
      font-size: 13px;
    }

    #billText {
      font-size: 15px;
    }
  }

  /* ============================================
     Card Content Spacing
     ============================================ */
  #feedContainerFeedPage .card-body {
    padding: 0;
    margin-bottom: 0.75rem;
  }

  #feedContainerFeedPage .card-title {
    margin-bottom: 0.5rem;
  }

  /* ============================================
     Metadata Text
     ============================================ */
  .metadata-text {
    color: var(--text-tertiary);
    font-size: 14px;
    margin-bottom: 4px;
  }

  .date-text {
    color: var(--text-muted);
    font-size: 13px;
  }

  /* ============================================
     Light Theme Specific Overrides
     ============================================ */
  [data-theme="light"] #feedContainerFeedPage {
    box-shadow: 0 4px 6px var(--shadow-color);
  }

  [data-theme="light"] .button-container Button,
  [data-theme="light"] #billLink,
  [data-theme="light"] #viewBillButton,
  [data-theme="light"] #addToCollectionButton,
  [data-theme="light"] #shareButton {
    color: var(--text-primary) !important;
  }
`;
export default CollectionPage;